import { NodeModel, RenderParams } from "@minoru/react-dnd-treeview";
import { ArrowRight } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import React, { useCallback, useEffect } from "react";
import { FileProperties } from "../../models/FileProperties";
import { TypeIcon } from "./TypeIcon";

const customStyles = `
  ul, ol {
    list-style-type: none;
    margin: 0;
    padding: 0;
  }
`;

type Props = RenderParams & {
	openAssets: { [assetId: number | string]: number | string };
	setOpenAssets: (assets: {
		[assetId: number | string]: number | string;
	}) => void;
	node: NodeModel<FileProperties>;
	setContextMenuOver: (
		data: NodeModel<FileProperties>,
		e: React.MouseEvent<HTMLElement>,
	) => void;
	onDoubleClick: (
		data: NodeModel<FileProperties>,
		e: React.MouseEvent<HTMLElement>,
	) => void;
	isSelected: boolean;
	isDragging: boolean;
	testIdPrefix?: string;
	onClick: (e: React.MouseEvent, node: NodeModel<FileProperties>) => void;
	"data-node-id"?: string;
};

export const CustomNode = ({
	openAssets,
	setOpenAssets,
	setContextMenuOver,
	onDoubleClick,
	testIdPrefix = "",
	isSelected,
	...props
}: Props) => {
	const { id, droppable, data } = props.node;
	const indent = props.depth * 24;

	const handleClick = (e: React.MouseEvent) => {
		props.onClick(e, props.node);
	};

	const handleToggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (openAssets[id] === id) {
			const newOpenAssets = { ...openAssets };
			delete newOpenAssets[id as number];
			setOpenAssets(newOpenAssets);
		} else {
			setOpenAssets({ ...openAssets, [id]: id });
		}
		props.onToggle();
	};

	const handleContextMenu = useCallback(
		(ev: any) => {
			ev.stopPropagation();
			ev.preventDefault();
			setContextMenuOver(props.node, ev);
		},
		[id, setContextMenuOver],
	);

	const handleViewAssetDialog = useCallback(
		(e: any) => {
			e.stopPropagation();
			e.preventDefault();
			onDoubleClick(props.node, e);
			setContextMenuOver(
				{
					id: "",
					parent: "",
					text: "",
					droppable: false,
				},
				e,
			);
		},
		[id, onDoubleClick],
	);

	useEffect(() => {
		props.containerRef.current?.addEventListener(
			"contextmenu",
			handleContextMenu,
		);
		props.containerRef.current?.addEventListener(
			"dblclick",
			handleViewAssetDialog,
		);

		return () => {
			props.containerRef.current?.removeEventListener(
				"contextmenu",
				handleContextMenu,
			);
			props.containerRef.current?.removeEventListener(
				"dblclick",
				handleViewAssetDialog,
			);
		};
	}, []);

	return (
		<Box
			sx={{
				alignItems: "center",
				display: "flex",
				height: "32px",
				listStyleType: "none",
				backgroundColor: isSelected ? "#e8f0fe" : "",
			}}
			style={{ paddingInlineStart: indent }}
			data-node-id={id}
			onClick={handleClick}
			title={`${props.node.id} - ${props.node.text}`}
		>
			<style>{customStyles}</style>
			{props.node.droppable ? (
				<Box
					sx={{
						alignItems: "center",
						cursor: "pointer",
						display: "flex",
						height: "24px",
						justifyContent: "center",
						width: "24px",
						transition: "transform linear 0.1s",
						transform: props.isOpen ? "rotate(90deg)" : "rotate(0deg)",
					}}
				>
					<Box onClick={handleToggle}>
						<ArrowRight data-testid={`arrow-right-icon-${id}`} />
					</Box>
				</Box>
			) : null}
			<Box
				sx={{
					display: "flex",
				}}
			>
				<TypeIcon droppable={droppable as boolean} fileType={data?.fileType} />
			</Box>
			<Box
				sx={{
					paddingInlineStart: "8px",
				}}
			>
				<Typography variant="body2">{props.node.text}</Typography>
			</Box>
		</Box>
	);
};
